import { useState } from "react";
import { ChevronDown, ChevronRight, Info, AlertTriangle, Lightbulb } from "lucide-react";
import { colors } from "../../../styles/theme";

/**
 * Parse inline **bold** and `code` markers into React elements.
 */
export function renderInlineText(text) {
  if (!text) return null;
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Check for bold first
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const codeMatch = remaining.match(/`([^`]+)`/);

    let firstMatch = null;
    let matchType = null;

    if (boldMatch && (!codeMatch || boldMatch.index <= codeMatch.index)) {
      firstMatch = boldMatch;
      matchType = "bold";
    } else if (codeMatch) {
      firstMatch = codeMatch;
      matchType = "code";
    }

    if (!firstMatch) {
      parts.push(remaining);
      break;
    }

    if (firstMatch.index > 0) {
      parts.push(remaining.slice(0, firstMatch.index));
    }

    if (matchType === "bold") {
      parts.push(<strong key={key++}>{firstMatch[1]}</strong>);
    } else {
      parts.push(
        <code
          key={key++}
          style={{
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: 4,
            padding: "1px 5px",
            fontSize: "0.9em",
            fontFamily: "monospace",
          }}
        >
          {firstMatch[1]}
        </code>
      );
    }

    remaining = remaining.slice(firstMatch.index + firstMatch[0].length);
  }

  return parts;
}

export function Paragraph({ text }) {
  return (
    <p style={{ lineHeight: 1.7, marginBottom: 12, color: colors.text }}>
      {renderInlineText(text)}
    </p>
  );
}

export function Heading({ text }) {
  return (
    <h4
      style={{
        fontSize: 16,
        fontWeight: 600,
        marginTop: 20,
        marginBottom: 8,
        color: colors.text,
      }}
    >
      {renderInlineText(text)}
    </h4>
  );
}

export function StepList({ items }) {
  return (
    <ol
      style={{
        paddingLeft: 24,
        marginBottom: 16,
        lineHeight: 1.7,
        color: colors.text,
      }}
    >
      {items.map((item, i) => (
        <li key={i} style={{ marginBottom: 6 }}>
          {renderInlineText(item)}
        </li>
      ))}
    </ol>
  );
}

export function Table({ headers, rows }) {
  return (
    <div style={{ overflowX: "auto", marginBottom: 16 }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 14,
        }}
      >
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                style={{
                  textAlign: "left",
                  padding: "8px 12px",
                  borderBottom: `2px solid ${colors.border}`,
                  fontWeight: 600,
                  color: colors.text,
                  whiteSpace: "nowrap",
                }}
              >
                {renderInlineText(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  style={{
                    padding: "8px 12px",
                    borderBottom: `1px solid ${colors.border}`,
                    color: colors.text,
                    verticalAlign: "top",
                  }}
                >
                  {renderInlineText(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CALLOUT_STYLES = {
  info: { bg: "#e8f4fd", border: "#b8daff", icon: Info, iconColor: "#0c5460" },
  warning: { bg: "#fff3cd", border: "#ffc107", icon: AlertTriangle, iconColor: "#856404" },
  tip: { bg: "#d4edda", border: "#28a745", icon: Lightbulb, iconColor: "#155724" },
};

export function Callout({ variant = "info", text }) {
  const style = CALLOUT_STYLES[variant] || CALLOUT_STYLES.info;
  const Icon = style.icon;

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        padding: "12px 16px",
        marginBottom: 16,
        borderLeft: `4px solid ${style.border}`,
        background: style.bg,
        borderRadius: 4,
        fontSize: 14,
        lineHeight: 1.6,
      }}
    >
      <Icon size={18} color={style.iconColor} style={{ flexShrink: 0, marginTop: 2 }} />
      <span style={{ color: style.iconColor }}>{renderInlineText(text)}</span>
    </div>
  );
}

export function FAQ({ items }) {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div style={{ marginBottom: 16 }}>
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div
            key={i}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              marginBottom: 8,
              overflow: "hidden",
            }}
          >
            <button
              onClick={() => setOpenIndex(isOpen ? null : i)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                background: isOpen ? colors.bg : "white",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                fontSize: 14,
                fontWeight: 500,
                color: colors.text,
              }}
            >
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              {renderInlineText(item.question)}
            </button>
            {isOpen && (
              <div
                style={{
                  padding: "10px 14px 14px 38px",
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: colors.textSecondary || "#666",
                }}
              >
                {renderInlineText(item.answer)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function CodeBlock({ text }) {
  return (
    <pre
      style={{
        background: "#1e1e1e",
        color: "#d4d4d4",
        padding: "14px 16px",
        borderRadius: 6,
        fontSize: 13,
        lineHeight: 1.5,
        overflowX: "auto",
        marginBottom: 16,
        fontFamily: "monospace",
      }}
    >
      {text}
    </pre>
  );
}

const BLOCK_RENDERERS = {
  paragraph: Paragraph,
  heading: Heading,
  steps: StepList,
  table: Table,
  callout: Callout,
  faq: FAQ,
  code: CodeBlock,
};

export function ContentBlock({ block }) {
  const Renderer = BLOCK_RENDERERS[block.type];
  if (!Renderer) return null;
  return <Renderer {...block} />;
}
